import { useState } from 'react';
import { useI18n } from '../../i18n';
import { dtfmt } from '../../lib/format';
import { api } from '../../lib/http';
import { Loading, useLoad } from '../ui';

interface Token { id: string; name: string; createdAt: string; lastUsedAt: string | null }
interface AutoKey { active: boolean; createdAt?: string; lastUsedAt?: string | null; expiresAt?: string }

const SHAREPOINT = 'https://smtsoftwareservices.sharepoint.com/sites/ProjectDelivery';

/** Automatisierungs-Schlüssel für die geplanten Läufe in Chrome: einrichten, Zeile für die SharePoint-Konsole, widerrufen. */
function AutomationKey() {
  const { t } = useI18n();
  const { data, error, reload } = useLoad<AutoKey>('/api/admin/automation-key');
  const [line, setLine] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    setErr(null); setCopied(false);
    try {
      const r = await api<{ key: string; storageKey: string }>('POST', '/api/admin/automation-key');
      setLine(`localStorage.setItem(${JSON.stringify(r.storageKey)}, ${JSON.stringify(r.key)}); 'OK'`);
      reload();
    } catch (x) { setErr((x as Error).message); }
  }
  async function revoke() {
    setErr(null);
    try { await api('DELETE', '/api/admin/automation-key'); setLine(null); reload(); } catch (x) { setErr((x as Error).message); }
  }
  async function copy() {
    if (!line) return;
    try { await navigator.clipboard.writeText(line); setCopied(true); } catch { setCopied(false); }
  }

  return (
    <div className="box" style={{ padding: 14, margin: '16px 0' }}>
      <h3 style={{ marginTop: 0 }}>{t('auto.title')}</h3>
      <p className="muted">{t('auto.lead')}</p>
      {!data ? <Loading error={error} /> : (
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{data.active ? <>{t('auto.active')} · {t('auto.expires')} {dtfmt(data.expiresAt!)} · {t('tokens.used')}: {data.lastUsedAt ? dtfmt(data.lastUsedAt) : t('tokens.never')}</> : <span className="muted">{t('auto.none')}</span>}</span>
          <button className="btn primary" onClick={create}>{data.active ? t('auto.renew') : t('auto.create')}</button>
          {data.active && <button className="btn danger" onClick={revoke}>{t('auto.revoke')}</button>}
        </div>
      )}
      {err && <p className="err">{err}</p>}
      {line && (
        <div role="status" style={{ marginTop: 12 }}>
          <p style={{ marginTop: 0 }}>{t('auto.steps')}</p>
          <ol>
            <li>{t('auto.step1')} <a href={SHAREPOINT} target="_blank" rel="noreferrer">{t('auto.openSp')}</a></li>
            <li>{t('auto.step2')}</li>
            <li>{t('auto.step3')}</li>
          </ol>
          <div className="tokenbox">{line}</div>
          <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button className="btn" onClick={copy}>{copied ? t('auto.copied') : t('auto.copyLine')}</button>
            <span className="muted">{t('auto.once')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** API-Schlüssel der angemeldeten Admin-Person (z. B. für den Bestellordner-Scan). */
export function TokensAdmin() {
  const { t } = useI18n();
  const { data, error, reload } = useLoad<Token[]>('/api/admin/tokens');
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try { const r = await api<{ token: string }>('POST', '/api/admin/tokens', { name: name.trim() }); setFresh(r.token); setName(''); reload(); } catch (x) { setErr((x as Error).message); }
  }

  return (
    <>
      <p className="muted lead">{t('tokens.lead')}</p>
      <form className="row" onSubmit={create} style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="fld" style={{ flex: 1, maxWidth: 420 }}><label htmlFor="tk-n">{t('tokens.name')}</label><input id="tk-n" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('tokens.namePh')} /></div>
        <button className="btn primary" disabled={!name.trim()}>{t('tokens.add')}</button>
      </form>
      {err && <p className="err">{err}</p>}
      {fresh && <div className="box" style={{ padding: 14 }} role="status"><p style={{ marginTop: 0 }}>{t('tokens.copy')}</p><div className="tokenbox">{fresh}</div></div>}
      <AutomationKey />
      {!data ? <Loading error={error} /> : data.length > 0 && (
        <div className="box tablewrap">
          <table>
            <thead><tr><th>{t('tokens.name')}</th><th>{t('tokens.created')}</th><th>{t('tokens.used')}</th><th /></tr></thead>
            <tbody>{data.map((k) => (
              <tr key={k.id}><td>{k.name}</td><td className="num">{dtfmt(k.createdAt)}</td><td className="num">{k.lastUsedAt ? dtfmt(k.lastUsedAt) : <span className="muted">{t('tokens.never')}</span>}</td>
                <td className="r"><button className="btn danger" onClick={() => api('DELETE', `/api/admin/tokens/${k.id}`).then(reload)}>{t('tokens.revoke')}</button></td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
